// src/contexts/ReportContext.tsx
import React, { createContext, useContext, useState } from 'react';

export type DatePeriodKey = 'last_30_days' | 'this_month' | 'last_month' | 'this_week' | 'custom';
export type ComparisonPeriodKey = 'last_month' | 'last_year_same_month' | 'custom';

interface ReportFilterContextType {
  selectedPeriod: DatePeriodKey;
  setSelectedPeriod: (val: DatePeriodKey) => void;
  customStart: string;
  setCustomStart: (val: string) => void;
  customEnd: string;
  setCustomEnd: (val: string) => void;
  showComparison: boolean;
  setShowComparison: (val: boolean) => void;
  comparisonPeriod: ComparisonPeriodKey;
  setComparisonPeriod: (val: ComparisonPeriodKey) => void;
}

const ReportContext = createContext<ReportFilterContextType | undefined>(undefined);

const getFormattedDate = (daysAgo: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const ReportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<DatePeriodKey>('last_30_days');

  const [customStart, setCustomStart] = useState(getFormattedDate(2));
  const [customEnd, setCustomEnd] = useState(getFormattedDate(1));

  const [showComparison, setShowComparison] = useState(false);
  const [comparisonPeriod, setComparisonPeriod] = useState<ComparisonPeriodKey>('last_month');

  return (
    <ReportContext.Provider value={{
      selectedPeriod, setSelectedPeriod,
      customStart, setCustomStart,
      customEnd, setCustomEnd,
      showComparison, setShowComparison,
      comparisonPeriod, setComparisonPeriod
    }}>
      {children}
    </ReportContext.Provider>
  );
};

export const useReportFilter = () => {
  const context = useContext(ReportContext);
  if (!context) throw new Error('useReportFilter must be used within a ReportProvider');
  return context;
};
